import type { User } from "@supabase/supabase-js";

/**
 * Resolve a display avatar URL from a Supabase Auth user.
 * Prefer Google / OAuth picture fields; never trust non-https values.
 */
export function getUserAvatarUrl(user: User | null | undefined): string | null {
  if (!user) return null;

  const candidates: unknown[] = [
    user.user_metadata?.avatar_url,
    user.user_metadata?.picture,
    user.user_metadata?.avatar,
  ];

  for (const identity of user.identities ?? []) {
    const data = identity.identity_data;
    if (!data || typeof data !== "object") continue;
    const record = data as Record<string, unknown>;
    candidates.push(record.avatar_url, record.picture, record.avatar);
  }

  for (const value of candidates) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    try {
      const url = new URL(trimmed);
      if (url.protocol !== "https:") continue;
      return url.toString();
    } catch {
      // invalid URL
    }
  }

  return null;
}

/** Two-letter initials from email local-part (fallback when no photo). */
export function getUserInitials(email: string | null | undefined): string {
  if (!email) return "U";
  /* v8 ignore next -- residual branch: split always yields ≥1 element */
  const local = email.split("@")[0]?.trim() ?? "";
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  if (local.length === 1) return local.toUpperCase();
  /* v8 ignore next -- residual branch: non-empty email implies non-empty slice */
  return email.slice(0, 2).toUpperCase() || "U";
}
